import React, { useState, useEffect, useCallback } from 'react';
import './UserManagement.css';

// ─── Admin view: approve / reject pending family members ────────────────────
const AdminPanel = ({ pending, approved, onApprove, onReject, loading }) => (
  <div className="user-management">
    <div className="admin-header">
      <h2>👥 Family Account Management</h2>
      <p className="admin-subtitle">Approve or reject family member access requests</p>
    </div>

    <div className="management-sections">
      {/* Pending Approvals */}
      <div className="pending-section">
        <h3>⏳ Pending Approvals {pending.length > 0 && <span className="badge">{pending.length}</span>}</h3>
        {pending.length === 0 ? (
          <p className="no-pending">✅ No pending requests</p>
        ) : (
          <div className="pending-list">
            {pending.map(user => (
              <div key={user.id} className="pending-user-card">
                <div className="user-info">
                  <h4>{user.name}</h4>
                  <p className="user-email">{user.email}</p>
                  <p className="user-date">Requested: {new Date(user.requestedAt).toLocaleString()}</p>
                </div>
                <div className="user-actions">
                  <button className="approve-btn" onClick={() => onApprove(user.id)} disabled={loading}>
                    ✅ Approve
                  </button>
                  <button className="reject-btn" onClick={() => onReject(user.id)} disabled={loading}>
                    ❌ Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approved Members */}
      <div className="approved-section">
        <h3>✅ Approved Family Members ({approved.length})</h3>
        {approved.length === 0 ? (
          <p className="no-pending">No approved members yet</p>
        ) : (
          <div className="users-list">
            {approved.map(user => (
              <div key={user.id} className="user-card">
                <div className="user-info">
                  <h4>{user.name}</h4>
                  <p className="user-email">{user.email}</p>
                  <p className="user-date">Approved: {new Date(user.approvedAt).toLocaleString()}</p>
                </div>
                <span className="status-badge approved">approved</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

// ─── Register / Login form for family members ────────────────────────────────
const FamilyLoginForm = ({ onLoginSuccess }) => {
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'pending'
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const res = await fetch('/api/family/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password })
      });
      const data = await res.json();
      if (data.success) {
        onLoginSuccess(data.user);
      } else {
        setError(data.error || 'Login failed — check your email/password or wait for approval');
      }
    } catch { setError('Could not reach server'); }
    setBusy(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const res = await fetch('/api/family/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setMode('pending');
      } else {
        setError(data.error || 'Registration failed');
      }
    } catch { setError('Could not reach server'); }
    setBusy(false);
  };

  if (mode === 'pending') {
    return (
      <div className="login-form-container">
        <div className="login-form pending-notice">
          <div className="pending-icon">⏳</div>
          <h2>Request Submitted!</h2>
          <p>Your access request has been sent to the admin (Jan Marie).</p>
          <p>You'll be able to log in once it's approved.</p>
          <button className="submit-btn" onClick={() => setMode('login')}>Back to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-form-container">
      <div className="login-form">
        <div className="family-brand">
          <div className="brand-icon">👨‍👩‍👧‍👦</div>
          <h2>Knox Family Bot</h2>
          <p>Private family trading system</p>
        </div>

        <div className="form-tabs">
          <button className={`tab ${mode === 'login' ? 'active' : ''}`} onClick={() => { setMode('login'); setError(''); }}>
            Login
          </button>
          <button className={`tab ${mode === 'register' ? 'active' : ''}`} onClick={() => { setMode('register'); setError(''); }}>
            Request Access
          </button>
        </div>

        <form onSubmit={mode === 'login' ? handleLogin : handleRegister}>
          {mode === 'register' && (
            <div className="form-group">
              <label>Full Name</label>
              <input type="text" value={formData.name} onChange={set('name')} required placeholder="Your full name" />
            </div>
          )}
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={formData.email} onChange={set('email')} required placeholder="your@email.com" />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input type="password" value={formData.password} onChange={set('password')} required placeholder="Choose a password" />
          </div>

          {error && <div className="form-error">⚠️ {error}</div>}

          <button type="submit" className="submit-btn" disabled={busy}>
            {busy ? '...' : mode === 'login' ? 'Sign In' : 'Request Access'}
          </button>
        </form>

        {mode === 'register' && (
          <p className="register-note">
            Your request will be reviewed by the admin before you can access the bot.
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Logged-in family member view ────────────────────────────────────────────
const MemberView = ({ user, onLogout }) => (
  <div className="user-management">
    <div className="admin-header">
      <h2>Welcome, {user.name}! 👋</h2>
      <button className="logout-btn" onClick={onLogout}>Logout</button>
    </div>
    <div className="member-info-card">
      <p><strong>Email:</strong> {user.email}</p>
      <p><strong>Role:</strong> {user.role || 'Family Member'}</p>
      <p><strong>Status:</strong> <span className="status-badge approved">approved</span></p>
    </div>
    <p className="member-note">Use the <strong>💰 Trading</strong> tab to view and place trades.</p>
  </div>
);

// ─── Main component ──────────────────────────────────────────────────────────
const UserManagement = ({ isAdminView = false }) => {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [familyUser, setFamilyUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('family_session') || 'null'); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [pRes, aRes] = await Promise.all([
        fetch('/api/family/pending'),
        fetch('/api/family/users')
      ]);
      const pData = await pRes.json();
      const aData = await aRes.json();
      if (pData.success) setPending(pData.pending);
      if (aData.success) setApproved(aData.users);
    } catch { /* silent */ }
  }, []);

  // Admin view: always load data and poll for new requests
  useEffect(() => {
    if (isAdminView) {
      loadData();
      const interval = setInterval(loadData, 15000);
      return () => clearInterval(interval);
    }
  }, [isAdminView, loadData]);

  const handleApprove = async (id) => {
    setLoading(true);
    try {
      const res = await fetch('/api/family/approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      const data = await res.json();
      if (data.success) { alert(`✅ ${data.message}`); await loadData(); }
      else alert('❌ ' + (data.error || 'Approve failed'));
    } catch { alert('❌ Server error'); }
    setLoading(false);
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this request?')) return;
    setLoading(true);
    try {
      const res = await fetch('/api/family/reject', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      const data = await res.json();
      if (data.success) { await loadData(); }
      else alert('❌ ' + (data.error || 'Reject failed'));
    } catch { alert('❌ Server error'); }
    setLoading(false);
  };

  const handleLoginSuccess = (user) => {
    setFamilyUser(user);
    localStorage.setItem('family_session', JSON.stringify(user));
  };

  const handleLogout = () => {
    setFamilyUser(null);
    localStorage.removeItem('family_session');
  };

  // Admin view (Jan Marie sees this when she opens the Family tab)
  if (isAdminView) {
    return <AdminPanel pending={pending} approved={approved} onApprove={handleApprove} onReject={handleReject} loading={loading} />;
  }

  // Family member view
  if (familyUser) {
    return <MemberView user={familyUser} onLogout={handleLogout} />;
  }

  return <FamilyLoginForm onLoginSuccess={handleLoginSuccess} />;
};

export default UserManagement;

